import React, {useCallback, useMemo, useState} from "react";
import "./index.scss";
import {connect} from "react-redux";
import {LoggedInLayout} from "features/layouts/pages/logged-in";
import Title from "features/components/title";
import {__} from "services/i18n";
import {RadioSelection} from "features/components/radio";
import Text from "features/components/text";
import {
    THREAD_TYPES,
    THREAD_ACL_TYPES,
    COMPANY_ROLES,
    SUBSCRIPTION_TYPES,
    MOBILE_MEDIA_QUERY_THRESHOLD
} from "constants.js";
import {TextField} from "features/components/text-field";
import TextArea from "features/components/text-area";
import ChipSelector from "features/components/chip-selector";
import {staticDataInterests, staticDataOsaOffices} from "store/reducer/static/selectors";
import {downloadStaticData} from "store/reducer/static/reducer";
import Button from "features/components/button";
import Select from "features/components/select";
import {Icon} from "features/components/icon";
import {get} from "lodash";
import {startSingleThread} from "store/reducer/threads/single/reducer";
import {singleThreadSelector} from "store/reducer/threads/single/selectors";
import {upsertThreadErrorSelector, upsertThreadLoadingSelector} from "store/reducer/threads/upsert/selectors";
import {upsertThreadStart} from "store/reducer/threads/upsert/reducer";
import {deleteThreadErrorSelector, deleteThreadLoadingSelector} from "store/reducer/threads/delete/selectors";
import {deleteThreadStart} from "store/reducer/threads/delete/reducer";
import {profileSpecialRequestCount} from "store/reducer/user/profile/selectors";
import {getProfileStart} from "store/reducer/user/profile/reducer";
import {getUserSubscriptionStart} from "store/reducer/user/subscription/reducer";
import {getUserSubscriptionType} from "store/reducer/user/subscription/selectors";
import {useMediaQuery} from "services/utils/utils";

export const ThreadUpsertPage = (props) => {

    const {interestsData, osaOffices, match, thread, loading, error, delete_loading, delete_error, special_remaining, subscription_type} = props;
    const {startSingleThread, downloadStaticData, upsertThreadStart, deleteThreadStart, getProfileStart, getUserSubscriptionStart} = props;

    const [type, setType] = useState(THREAD_TYPES.thread);
    const [title, setTitle] = useState("");
    const [message, setMessage] = useState("");
    const [attachment, setAttachment] = useState(null);
    const [interestsVal, setInterests] = useState([]);
    const [aclType, setAclType] = useState(THREAD_ACL_TYPES.everyone);
    const [officesVal, setOffices] = useState([]);
    const [rolesVal, setRoles] = useState([]);

    const isMobile = useMediaQuery(MOBILE_MEDIA_QUERY_THRESHOLD)

    React.useEffect(() => {
        downloadStaticData();
        getProfileStart();
        getUserSubscriptionStart();
    }, [downloadStaticData, getProfileStart, getUserSubscriptionStart]);

    const threadId = useMemo(() => {
        return get(match, 'params.id', null);
    }, [match]);

    React.useEffect(() => {
        if(threadId) startSingleThread({id: threadId});
    }, [threadId, startSingleThread]);

    React.useEffect(() => {
        if(thread && threadId) {
            setTitle(thread.title);
            setMessage(thread.message);
            setType(thread.type);
            setInterests(thread.categories);
        }
    }, [thread, threadId]);

    const isEdit = useMemo(() => {
        return !!threadId;
    }, [threadId]);


    const renderTypeSelection = useMemo(() => {
        return <>
            <Title size={18}>{__("thread-upsert.select-type")}</Title>
            {
                [THREAD_TYPES.thread, THREAD_TYPES.request].map(i => {
                    return <RadioSelection
                        isSelected={type === i}
                        onSelection={() => setType(i)}
                        key={i}
                    >
                        <Text size={16}>{__(`thread-upsert.types.${i}`)}</Text>
                    </RadioSelection>
                })
            }
        </>
    }, [type]);


    const input = React.useRef(null);
    /*istanbul ignore next*/
    const selectAttachment = useCallback(() => {
        input.current.click();
    }, [input]);
    /*istanbul ignore next*/
    const fileSelected = useCallback((event) => {
        const file = event.target.files[0];
        if (!file) {
            return;
        }
        const reader = new FileReader();
        reader.readAsDataURL(file);
        /* istanbul ignore next */
        reader.onload = (event) => {
            setAttachment({
                name: file.name,
                type: file.type,
                src: event.target.result
            })
        }
    }, [setAttachment]);

    const renderData = useMemo(() => {
        return <>
            <TextField type={"text"} value={title} onChange={setTitle} placeholder={__("text-fields.thread-title.placeholder")} />
            <div className="with-attachment">
                <TextArea value={message} onChange={setMessage} placeholder={__("thread-upsert.message-placeholder")} />
                <div className="attachment" onClick={selectAttachment}><Icon>attachment</Icon></div>
            </div>
            <input type="file"
                   accept={[]}
                   ref={input}
                   className="file-upload"
                   onChange={fileSelected}
            />
            {/*istanbul ignore next*/attachment && <div className={"attachment-selected"}><div onClick={() => setAttachment(null)}><Icon>close</Icon></div><Text size={16}>Allegato: {attachment.name}</Text></div>}
        </>
    }, [title, message, selectAttachment, fileSelected, attachment]);


    const renderCategories = useMemo(() => {
        return <>
            <Title size={18}>{__("thread-upsert.select-categories.title")}</Title>
            <ChipSelector
                placeholder={__("thread-upsert.select-categories.placeholder")}
                data={interestsData}
                enableAddSearched={false}
                value={interestsVal}
                onChange={setInterests}
                maxItems={10}
            />
        </>
    }, [interestsVal, setInterests, interestsData]);
    /*istanbul ignore next*/
    React.useEffect(() => {
        if(type === THREAD_TYPES.thread) {
            setAclType(THREAD_ACL_TYPES.everyone);
        }
    }, [type]);
    /*istanbul ignore next*/
    React.useEffect(() => {
        if(aclType !== THREAD_ACL_TYPES.selective) {
            setOffices([]);
            setRoles([]);
        }
    }, [aclType])

    const renderAcl = useMemo(() => {
        if(isEdit || [SUBSCRIPTION_TYPES.CerchiaAudaci, SUBSCRIPTION_TYPES.OsaDigital].includes(subscription_type)) return null;
        return <div className={"thread-acl"}>
            <Title size={18}>{__("thread-upsert.acl-title")}</Title>
            <RadioSelection
                isSelected={aclType === THREAD_ACL_TYPES.everyone}
                onSelection={() => setAclType(THREAD_ACL_TYPES.everyone)}
            >
                <Text size={16}>{__(`thread-upsert.acl-types.everyone`)}</Text>
            </RadioSelection>
            <RadioSelection
                isSelected={aclType === THREAD_ACL_TYPES.selective}
                onSelection={() => setAclType(THREAD_ACL_TYPES.selective)}
                disabled={type === THREAD_TYPES.thread}
            >
                <Text size={16}>{__(`thread-upsert.acl-types.selective`)}</Text>
                <div className={"selective"}>
                    <Select
                        value={officesVal}
                        values={osaOffices}
                        onChange={setOffices}
                        placeholder={__("text-fields.osa-offices.placeholder")}
                        multiple={true}
                    />
                    <Select
                        value={rolesVal}
                        values={COMPANY_ROLES}
                        onChange={setRoles}
                        placeholder={__("text-fields.roles.placeholder")}
                        multiple={true}
                    />
                </div>
            </RadioSelection>
            <RadioSelection
                isSelected={aclType === THREAD_ACL_TYPES.founders}
                onSelection={() => setAclType(THREAD_ACL_TYPES.founders)}
                disabled={/* istanbul ignore next */type === THREAD_TYPES.thread || special_remaining === 0}
            >
                <Text size={16}>{__(`thread-upsert.acl-types.${special_remaining === -1 ? "founders-knight" : "founders"}`, [{key: "[count]", value: special_remaining}])}</Text>
            </RadioSelection>
        </div>;
    }, [aclType, isEdit, officesVal, osaOffices, rolesVal, type, special_remaining, subscription_type]);

    const doUpsert = useCallback(() => {
        /* istanbul ignore next */
        const thread = {
            id: threadId,
            title: title,
            type: type,
            message: message,
            visible_for: {
                type: aclType,
                roles: rolesVal.length === 0 ? ["all"] : rolesVal,
                hqs: officesVal.length === 0 ? ["all"] : officesVal
            },
            categories: interestsVal,
            rawAttachment: attachment
        }
        upsertThreadStart(thread)
    }, [threadId, title, type, message, aclType, rolesVal, officesVal, interestsVal, attachment, upsertThreadStart]);

    const renderButton = useMemo(() => {
        return <Button title={__(`thread-upsert.button.${isEdit ? "edit" : "publish"}`)} status={loading ? "loading" : "default"} onClick={doUpsert} type={"accent"} icon={"check"} />
    }, [isEdit, doUpsert, loading]);
    
    const renderContent = useMemo(() => {
        return <div className="content white-box">
                {renderTypeSelection}
                {renderData}
                {renderCategories}
                {renderAcl}
                {renderButton}
                {error.inError && <Text size={16} color={"danger"}>{__(error.error)}</Text>}
            </div>
    }, [renderTypeSelection, renderData, renderCategories, renderAcl, renderButton, error.inError, error.error]);

    const renderSidebar = useMemo(() => {
        return <div className="sidebar">
            {!isMobile && renderButton}
            <div className="tips">
                <Title size={20} align={"center"}>{__("thread-upsert.tips.title")}</Title>
                <Text size={16} align={"center"}>{__("thread-upsert.tips.text")}</Text>
            </div>
        </div>
    }, [renderButton, isMobile]);


    const [deleteConfirm, setDeleteConfirm] = useState(false);

    const doDeleteThread = useCallback(() => {
        if(!deleteConfirm) {
            setDeleteConfirm(true)
        }
        else {
            deleteThreadStart(threadId);
        }
    }, [deleteConfirm, deleteThreadStart, threadId]);

    return <LoggedInLayout
        type={"light"}
        className={"threads-upsert-page"}
        breadcrumbs={[{title: "common.home-breadcrumb", link:"/"}, {title: "threads.breadcrumb"}]}
    >
        <Title size={34}>{__(`thread-upsert.title.${isEdit ? "edit" : "new"}`)}</Title>
        <div className="sidebar-wrapper">
            {renderContent}
            {renderSidebar}
        </div>
        <div>
            {isEdit && <Button title={__(deleteConfirm ? "buttons.delete-thread-confirm" : "buttons.delete-thread")} onClick={doDeleteThread} icon={"delete"} status={delete_loading ? "loading" : "danger"} />}
            {delete_error.inError && <Text size={16} color={"danger"}>{__(delete_error.error)}</Text>}
        </div>
    </LoggedInLayout>
};

/* istanbul ignore next */
const mapStateToProps = state => ({
    interestsData: staticDataInterests(state),
    osaOffices: staticDataOsaOffices(state),
    thread: singleThreadSelector(state),
    loading: upsertThreadLoadingSelector(state),
    error: upsertThreadErrorSelector(state),
    delete_error: deleteThreadErrorSelector(state),
    delete_loading: deleteThreadLoadingSelector(state),
    special_remaining: profileSpecialRequestCount(state),
    subscription_type: getUserSubscriptionType(state)
});
/* istanbul ignore next */
const mapDispatchToProps = {
    downloadStaticData,
    startSingleThread,
    upsertThreadStart,
    deleteThreadStart,
    getProfileStart,
    getUserSubscriptionStart
};

export default connect(mapStateToProps, mapDispatchToProps)(ThreadUpsertPage)